import { Price } from 'interfaces/price'
import { LineItem } from 'interfaces/line-item'
import { ProductType } from 'interfaces/purchase-order'
import { DiscountCalculator } from './discount-calculator'
import { ProductHelper } from './product-helper'

export interface LineItemDisplay {
  name: string
  quantity: number
  price: number
  period: string
  total: number
  priceId: string
  discountedPriceId?: string
  productId: string
  discount?: number
  isAddon?: boolean
  parentProductType?: ProductType
  isManualDiscount?: boolean
}

/**
 * Service for managing line item calculations and transformations
 * Handles product bundle interactions, price lookups, and discount applications
 */
export class LineItemManager {
  /**
   * Get period label from a Stripe price object
   * Converts Stripe billing format to user-friendly format
   */
  static getPeriodLabel(price: Price): string {
    if (price.type === 'recurring' && price.recurring) {
      const { interval } = price.recurring
      const intervalCount = price.recurring.interval_count || 1

      if (intervalCount === 1) {
        return interval
      }
      return `${intervalCount} ${interval}s`
    }
    return 'one-time'
  }

  /**
   * Find a product in product bundle
   * Searches base product, addon products, and additional products
   */
  static findProduct(productBundle: any, productId: string): any {
    if (productBundle.base_product.id === productId) {
      return productBundle.base_product
    }
    return (
      productBundle.addon_products.find((p: any) => p.id === productId) ||
      productBundle.additional_products.find((p: any) => p.id === productId) ||
      null
    )
  }

  /**
   * Check if product is an addon
   */
  static isAddonProduct(productBundle: any, productId: string): boolean {
    return productBundle.addon_products.some((p: any) => p.id === productId)
  }

  /**
   * Extract display price from Stripe price object
   * Handles both regular prices and custom internal descriptions
   */
  static extractDisplayPrice(price: Price): number {
    const priceAmount = price.unit_amount ? price.unit_amount / 100 : 0

    if (price.unit_amount === null && price.metadata?.internal_description) {
      const priceMatch =
        price.metadata.internal_description.match(/\$[\d,]+\.?\d*/)
      if (priceMatch) {
        return parseFloat(priceMatch[0].replace(/[$,]/g, ''))
      }
    }

    return priceAmount
  }

  /**
   * Calculate total amount with discounts applied
   */
  static calculateTotalAmount(lineItems: LineItemDisplay[]): number {
    return lineItems.reduce(
      (sum, item) => sum + item.total * (1 - (item.discount || 0) / 100),
      0
    )
  }

  /**
   * Sort line items: base products first, then addons
   */
  static sortLineItems(lineItems: LineItemDisplay[]): LineItemDisplay[] {
    return [
      ...lineItems.filter((item) => !item.isAddon),
      ...lineItems.filter((item) => item.isAddon),
    ]
  }

  /**
   * Build line items from selected product items
   * Handles price lookup, discount calculation, and line item creation
   */
  static buildLineItems(
    selectedItems: {
      priceId: string
      quantity: number
      productId: string
      discount?: number
    }[],
    productBundle: any,
    existingSavedItems: Partial<LineItem>[],
    parentProductType?: ProductType | null,
    allowQuantityAdjusting?: boolean
  ): {
    lineItems: LineItemDisplay[]
    savedLineItems: Partial<LineItem>[]
  } {
    const newLineItems: LineItemDisplay[] = []
    const newSavedLineItems: Partial<LineItem>[] = []

    selectedItems.forEach((item) => {
      // Use ProductHelper service to get prices and product
      const price = ProductHelper.findPrice(
        productBundle,
        item.productId,
        item.priceId
      )
      const product = ProductHelper.findProduct(productBundle, item.productId)

      if (product && price) {
        const period = this.getPeriodLabel(price)
        const displayPrice = ProductHelper.extractDisplayPrice(price)
        const isAddon = ProductHelper.isAddonProduct(
          productBundle,
          item.productId
        )

        const autoDiscount = DiscountCalculator.getAutomaticDiscount(
          item.productId,
          period,
          newSavedLineItems
            .filter(
              (li): li is LineItem & { product_id: string } => !!li.product_id
            )
            .map((li) => {
              const liPrice = productBundle.prices[li.product_id!]?.find(
                (p: any) => p.id === li.price
              )
              return {
                productId: li.product_id!,
                period: liPrice ? this.getPeriodLabel(liPrice) : period,
              }
            })
        )

        // Get discounted price ID if automatic discount applies
        let discountedPriceId: string | undefined
        if (autoDiscount > 0) {
          discountedPriceId = DiscountCalculator.getDiscountPriceId(
            productBundle,
            period,
            autoDiscount,
            item.productId
          )
        }

        newLineItems.push({
          name: product.name,
          quantity: item.quantity,
          price: displayPrice,
          period,
          total: displayPrice * item.quantity,
          priceId: item.priceId,
          discountedPriceId: discountedPriceId,
          productId: item.productId,
          discount: autoDiscount,
          isAddon,
          parentProductType: parentProductType || undefined,
        })

        const savedLineItem = {
          price: discountedPriceId || item.priceId,
          product_id: item.productId,
          quantity: item.quantity,
          adjustable_quantity: {
            enabled: !!allowQuantityAdjusting && item.quantity < 999,
            minimum: allowQuantityAdjusting && item.quantity < 999 ? 1 : null,
            maximum: allowQuantityAdjusting && item.quantity < 999 ? 999 : null,
          },
        }

        newSavedLineItems.push(savedLineItem)
      }
    })

    return {
      lineItems: newLineItems,
      savedLineItems: newSavedLineItems,
    }
  }
}
